import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getAllVHNDData, getVillages } from '../services/authService';
import VhndTabs from '../components/VhndTabs';
import '.././styles/vhndupcoming.css';

const VHNDUpcoming = () => {
  const navigate = useNavigate();
  const [vhndData, setVhndData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [villages, setVillages] = useState([]);
  const [filters, setFilters] = useState({
    location: '',
    period: 'All',
    villageId: '',
    status: 'all',
  });
  const [searchInput, setSearchInput] = useState(''); // Separate state for search input
  const [searchTimeout, setSearchTimeout] = useState(null); // Track debounce timeout
  const limit = 10;
  const selectedVillageId = useSelector((state) => state.village.selectedVillageId);
  const ashaId = localStorage.getItem('asha_id');

  useEffect(() => {
    const fetchVHNDData = async () => {
      setLoading(true);
      try {
        const queryParams = {
          page: currentPage,
          limit,
          villageId:  filters.villageId,
          location: filters.location,
        duration: filters.period.toLowerCase().replace(/ /g, '_'),
          status: filters.status, 
        };
        const response = await getAllVHNDData(queryParams, ashaId, selectedVillageId);
        if (response && response.data && Array.isArray(response.data.data)) {
          setVhndData(response.data.data);
          setTotalPages(response.data.pagination.totalPages || 1);
        } else {
          setError('Invalid API response structure');
        }
      } catch (err) {
        console.error('API Error:', err);
        setError('Error fetching data: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    const fetchVillages = async () => {
      try {
        const response = await getVillages(ashaId);
        const villageList = response.data || [];
        setVillages(villageList);
      } catch (error) {
        console.error('Error fetching villages:', error);
      }
    };

    if (ashaId) {
      fetchVHNDData();
      fetchVillages();
    }
    console.log('filters', filters);
  }, [currentPage, filters, selectedVillageId, ashaId]);

  useEffect(() => {
    // Cleanup timeout on component unmount
    return () => {
      if (searchTimeout) clearTimeout(searchTimeout);
    };
  }, [searchTimeout]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    if (name === 'location') {
      setSearchInput(value); // Update input display immediately
      if (searchTimeout) clearTimeout(searchTimeout);
      const timeout = setTimeout(() => {
        if (value.trim() && value.length >= 3) {
          setFilters((prev) => ({
            ...prev,
            [name]: value,
          }));
          setCurrentPage(1);
        } else if (!value.trim()) {
          setFilters((prev) => ({
            ...prev,
            [name]: '',
          }));
          setCurrentPage(1);
        }
      }, 500);
      setSearchTimeout(timeout);
    } else {
      setFilters((prev) => ({
        ...prev,
        [name]: value,
      }));
      setCurrentPage(1);
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      setCurrentPage(page);
    }
  };

  const processVHNDData = (data) =>
    data.map((vhnd, index) => {
      const headerColors = ['#4CAF50', '#2196F3', '#9C27B0', '#F44336', '#FF9800', '#673AB7'];
      const statusColors = ['dot-green', 'dot-amber', 'dot-red'];
      const isToday = new Date(vhnd.vhnd_date).toDateString() === new Date().toDateString();
      const isCancelled = vhnd.status === 'Cancelled';
      const approvalStatus = vhnd.approval_status || 'Approved';
      return {
        priority: isToday ? 'High Priority' : null,
        status: isToday ? 'Today' : isCancelled ? 'Cancelled' : 'Upcoming',
        date: vhnd.vhnd_date
          ? new Date(vhnd.vhnd_date).toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })
          : 'N/A',
        location: vhnd.location || 'N/A',
        time: `${vhnd.start_time ? vhnd.start_time.slice(0, 5) : 'N/A'} - ${
          vhnd.end_time ? vhnd.end_time.slice(0, 5) : 'N/A'
        }`,
        services: (vhnd.VhndParticipations?.[0]?.VhndParticipantServices || []).map(
          (s) => s?.service_type || 'N/A'
        ) || ['Immunization', 'ANC Check-up'],
        officials: (vhnd.VhndStaffAttendances || []).map(
          (staff) => `${staff?.official_name || 'N/A'} (${staff?.official_role || 'N/A'})`
        ) || ['N/A'],
        beneficiaries: (vhnd.expected_beneficiaries || 0).toString() + '-' + (vhnd.expected_beneficiaries ? (vhnd.expected_beneficiaries + 20).toString() : '20'),
        tasks: [
          { name: 'Confirm health officials', status: 'Completed' },
          { name: 'Arrange vaccines', status: 'Pending' },
          { name: 'Confirm beneficiary list', status: 'Pending' },
        ],
        approval: approvalStatus,
        cancellationReason: isCancelled ? 'Venue unavailable' : null,
        rescheduledDate: isCancelled ? 'TBD' : null,
        headerColor: headerColors[index % headerColors.length],
        statusColor: statusColors[index % statusColors.length],
      };
    });

  const processedVHNDs = processVHNDData(vhndData);

  const renderPagination = () => {
    const buttons = [];
    const maxButtons = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxButtons - 1);

    if (endPage - startPage + 1 < maxButtons) {
      startPage = Math.max(1, endPage - maxButtons + 1);
    }

    buttons.push(
      <div
        key="prev"
        className={`pagination-item pagination-arrow p-2 rounded ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200'}`}
        onClick={() => handlePageChange(currentPage - 1)}
      >
        <i className="fas fa-chevron-left"></i>
      </div>
    );

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <div
          key={i}
          className={`pagination-item p-2 rounded ${i === currentPage ? 'bg-blue-600 text-white' : 'hover:bg-gray-200'}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </div>
      );
    }

    buttons.push(
      <div
        key="next"
        className={`pagination-item pagination-arrow p-2 rounded ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200'}`}
        onClick={() => handlePageChange(currentPage + 1)}
      >
        <i className="fas fa-chevron-right"></i>
      </div>
    );

    return buttons;
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div className="text-red-600">Error: {error}</div>;


  return (
    <>
      <div className="page-header">
        <h1 className="title text-2xl font-bold">VHND Management</h1>
        <div className="header-actions flex space-x-2">
          <button
            className="btn btn-outline flex items-center border border-blue-600 text-blue-600 px-4 py-2 rounded hover:bg-blue-50"
            onClick={() => console.log('Export Schedule clicked')}
          >
            <i className="fas fa-file-export mr-2"></i> Export Schedule
          </button>
          <button
            className="btn btn-primary flex items-center bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            onClick={() => navigate('/VHND-create')}
          >
            <i className="fas fa-plus mr-2"></i> Plan New VHND
          </button>
        </div>
      </div>

      <VhndTabs />

      <div className="filter-bar flex flex-wrap items-center gap-4 bg-gray-50 p-4 rounded-lg mb-4">
        <div className="search-field flex items-center bg-white border rounded px-3 py-2 w-full sm:w-auto">
          <i className="fas fa-search text-gray-400 mr-2"></i>
          <input
            type="text"
            name="location"
            placeholder="Search upcoming VHNDs..."
            value={searchInput}
            onChange={handleFilterChange}
            className="outline-none flex-1"
          />
        </div>
        <div className="filter-group flex items-center">
          <span className="filter-label text-gray-600 mr-2">Period:</span>
          <select
            name="period"
            value={filters.period}
            onChange={handleFilterChange}
            className="filter-select border rounded px-3 py-2"
          >
           
            {['All', 'Next 30 Days', 'Next 60 Days', 'Next 90 Days', 'Custom Range'].map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}       
          </select>
        </div>
        <div className="divider border-l h-6"></div>
        <div className="filter-group flex items-center">
          <span className="filter-label text-gray-600 mr-2">Location:</span>
          <select
            name="villageId"
            value={filters.villageId}
            onChange={handleFilterChange}
            className="filter-select border rounded px-3 py-2"
          >
            {villages.length > 0 ? (
              <>
                <option value="">All Locations</option>
                {villages.map((village) => (
                  <option key={village.village_id} value={village.village_id}>
                    {village.village_name}
                  </option>
                ))}
              </>
            ) : (
              <option>No locations available</option>
            )}
          </select>
        </div>
        <div className="divider border-l h-6"></div>
        <div className="filter-group flex items-center">
          <span className="filter-label text-gray-600 mr-2">Status:</span>
          <select
            name="status"
            value={filters.status}
            onChange={handleFilterChange}
            className="filter-select border rounded px-3 py-2"
          >
            {['all', 'Approved', 'Pending Approval', 'Draft', 'Cancelled'].map((option) => (
              <option key={option} value={option.toLowerCase()}>
                {option}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="summary-stats grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { icon: 'fa-calendar-alt', value: processedVHNDs.length.toString(), label: 'Upcoming VHNDs', bg: '#E3F2FD', color: '#2196F3' },
          { icon: 'fa-check-circle', value: processedVHNDs.filter(v => v.approval === 'Approved').length.toString(), label: 'Approved', bg: '#E8F5E9', color: '#4CAF50' },
          { icon: 'fa-clock', value: processedVHNDs.filter(v => v.approval === 'Pending Approval').length.toString(), label: 'Pending Approval', bg: '#FFF8E1', color: '#FFC107' },
          { icon: 'fa-calendar-day', value: processedVHNDs.filter(v => v.status === 'Today').length.toString(), label: 'VHNDs Today', bg: '#E8F5E9', color: '#4CAF50' },
        ].map((stat, index) => (
          <div key={index} className="stat-card flex items-center bg-white shadow-md rounded-lg p-4">
            <div className="stat-icon flex items-center justify-center w-12 h-12 rounded-full mr-4" style={{ backgroundColor: stat.bg, color: stat.color }}>
              <i className={`fas ${stat.icon}`}></i>
            </div>
            <div className="stat-content">
              <div className="stat-value font-bold text-xl">{stat.value}</div>
              <div className="stat-label text-gray-600">{stat.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="vhnd-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {processedVHNDs.map((vhnd, index) => (
          <div key={index} className="vhnd-card bg-white shadow-md rounded-lg overflow-hidden">
            {vhnd.priority && (
              <div className="priority-high flex items-center bg-red-100 text-red-800 p-2">
                <i className="fas fa-exclamation-triangle mr-2"></i> {vhnd.priority}
              </div>
            )}
            <div className="vhnd-header p-4 text-white" style={{ backgroundColor: vhnd.headerColor }}>
              <div className={`status-badge flex items-center px-2 py-1 rounded-full text-sm ${
                vhnd.status === 'Today' ? 'bg-green-600' : vhnd.status === 'Cancelled' ? 'bg-red-600' : 'bg-blue-600'
              }`}>
                <i className={`fas fa-${vhnd.status === 'Today' ? 'calendar-day' : vhnd.status === 'Cancelled' ? 'times-circle' : 'calendar-alt'} mr-1`}></i>
                {vhnd.status}
              </div>
              <div className="vhnd-date text-lg font-semibold mt-2">{vhnd.date}</div>
              <div className="vhnd-info mt-2">
                <div className="vhnd-location flex items-center">
                  <i className="fas fa-map-marker-alt mr-2"></i> {vhnd.location}
                </div>
                <div className="vhnd-location flex items-center">
                  <i className="fas fa-clock mr-2"></i> {vhnd.time}
                </div>
              </div>
            </div>
            <div className="vhnd-body p-4">
              <div className="service-list flex flex-wrap gap-2 mb-4">
                {vhnd.services.map((service, i) => (
                  <div key={i} className="service-tag flex items-center bg-gray-100 px-2 py-1 rounded">
                    <i className={`fas fa-${
                      service === 'Immunization' ? 'syringe' :
                      service === 'Growth Monitoring' ? 'weight' :
                      service === 'ANC Check-up' ? 'female' :
                      service === 'Medicine Distribution' ? 'pills' :
                      service === 'Nutrition Counseling' ? 'apple-alt' :
                      'book-medical'
                    } mr-1`}></i>
                    {service}
                  </div>
                ))}
              </div>
              {vhnd.cancellationReason ? (
                <>
                  <div className="info-row flex mb-2">
                    <div className="info-label font-semibold mr-2">Cancellation Reason:</div>
                    <div className="info-value text-red-600 font-medium">{vhnd.cancellationReason}</div>
                  </div>
                  <div className="info-row flex mb-2">
                    <div className="info-label font-semibold mr-2">Rescheduled Date:</div>
                    <div className="info-value text-green-600 font-medium">{vhnd.rescheduledDate}</div>
                  </div>
                </>
              ) : (
                <div className="info-row flex mb-2">
                  <div className="info-label font-semibold mr-2">Estimated Beneficiaries:</div>
                  <div className="info-value">{vhnd.beneficiaries}</div>
                </div>
              )}
              <div className="info-row mb-4">
                <div className="info-label font-semibold mb-1">Health Officials:</div>
                <div className="info-value">
                  <div className="staff-list flex flex-wrap gap-2">
                    {vhnd.officials.map((official, i) => (
                      <div key={i} className="staff-tag flex items-center bg-gray-100 px-2 py-1 rounded">
                        <i className={`fas fa-${
                          official.includes('(MO)') ? 'user-md' :
                          official.includes('(ANM)') ? 'user-nurse' :
                          'user'
                        } mr-1`}></i>
                        {official}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {vhnd.tasks && (
                <div className="task-list space-y-2">
                  {vhnd.tasks.map((task, i) => (
                    <div key={i} className="task-item flex items-center">
                      <i className={`fas fa-${task.status === 'Completed' ? 'check-square' : 'square'} task-icon mr-2`}></i>
                      <div className="task-name flex-1">{task.name}</div>
                      <div className="task-status text-sm text-gray-600">{task.status}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="vhnd-footer flex justify-between items-center p-4 bg-gray-50">
              <div className="approval-status flex items-center">
                <span className={`status-dot w-2 h-2 rounded-full mr-2 ${
                  vhnd.statusColor === 'dot-green' ? 'bg-green-600' :
                  vhnd.statusColor === 'dot-amber' ? 'bg-yellow-600' :
                  'bg-red-600'
                }`}></span>
                {vhnd.approval}
              </div>
              <div className="action-buttons flex space-x-2">
                <button className="btn btn-outline flex items-center border border-blue-600 text-blue-600 px-3 py-1 rounded text-sm hover:bg-blue-50">
                  <i className={`fas fa-${vhnd.status === 'Cancelled' ? 'calendar-alt' : 'clipboard-list'} mr-1`}></i>
                  {vhnd.status === 'Cancelled' ? 'Reschedule' : 'Checklist'}
                </button>
                <button className="btn btn-primary flex items-center bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700">
                  <i className="fas fa-eye mr-1"></i> Details
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="pagination flex items-center justify-center space-x-2 mt-6">
        {renderPagination()}
      </div>
    </>
  );
};

export default VHNDUpcoming;